import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AISettings, UpdateSettingFunction } from './types';
import { Bot, Sparkles, ShieldAlert, Settings2 } from 'lucide-react';

/**
 * O que faz: Seção de Personalidade da IA
 * Onde usa: AISettingsForm
 * 
 * Permite customizar como a IA se apresenta e se comporta nas conversas,
 * incluindo nome, prompts personalizados e tópicos restritos
 * 
 * REFATORADO: Campos em grid com ícones e visual premium
 */

interface AIPersonalitySectionProps {
  settings: AISettings;
  updateSetting: UpdateSettingFunction;
  readOnly?: boolean;
}

export const AIPersonalitySection = ({
  settings,
  updateSetting,
  readOnly = false
}: AIPersonalitySectionProps) => {
  return (
    <Card className="rounded-2xl border-border shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <Bot className="w-5 h-5 text-accent-foreground" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold text-foreground">
              Personalidade da IA
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-0.5">
              Defina como a IA se apresenta e se comporta
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Grid de 2 colunas em desktop para campos menores */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Nome da IA */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-accent flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-accent-foreground" />
              </div>
              <Label htmlFor="ai-name" className="font-medium text-foreground">
                Nome da IA
              </Label>
            </div>
            <Input
              id="ai-name"
              placeholder="Ex: Sofia, Ana, Assistente Virtual..."
              value={settings.ai_name}
              onChange={e => !readOnly && updateSetting('ai_name', e.target.value)}
              disabled={readOnly}
              className="rounded-xl h-11 bg-white border-border"
            />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Como a IA deve se apresentar aos leads e pacientes
            </p>
          </div>

          {/* Tópicos Restritos */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-danger-subtle flex items-center justify-center">
                <ShieldAlert className="w-4 h-4 text-danger-text" />
              </div>
              <Label htmlFor="ai-restricted-topics" className="font-medium text-foreground">
                Tópicos Restritos
              </Label>
            </div>
            <Textarea
              id="ai-restricted-topics"
              placeholder="Liste os assuntos que a IA NÃO deve abordar..."
              value={settings.ai_restricted_topics_prompt}
              onChange={e => !readOnly && updateSetting('ai_restricted_topics_prompt', e.target.value)}
              rows={5}
              disabled={readOnly}
              className="rounded-xl resize-y min-h-[120px] bg-white border-border"
            />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Assuntos que a IA deve evitar ou não responder
            </p>
          </div>
        </div>

        {/* Prompt da Clínica - Full width */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-info-subtle flex items-center justify-center">
              <Bot className="w-4 h-4 text-info-text" />
            </div>
            <Label htmlFor="ai-clinic-prompt" className="font-medium text-foreground">
              Prompt da Clínica
            </Label>
          </div>
          <Textarea
            id="ai-clinic-prompt"
            placeholder="Descreva como a IA deve se comportar, que informações sobre a clínica ela deve saber, como deve responder..."
            value={settings.ai_clinica_prompt}
            onChange={e => !readOnly && updateSetting('ai_clinica_prompt', e.target.value)}
            rows={8}
            disabled={readOnly}
            className="rounded-xl resize-y min-h-[200px] bg-white border-border"
          />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Instruções específicas sobre como a IA deve se comportar e que informações sobre sua clínica ela deve conhecer
          </p>
        </div>

        {/* Prompt Administrativo - Full width */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center">
              <Settings2 className="w-4 h-4 text-muted-foreground" />
            </div>
            <Label htmlFor="admin-prompt" className="font-medium text-foreground">
              Prompt Administrativo
            </Label>
            <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full">
              Avançado
            </span>
          </div>
          <Textarea
            id="admin-prompt"
            placeholder="Instruções técnicas e administrativas para a IA..."
            value={settings.admin_prompt}
            onChange={e => !readOnly && updateSetting('admin_prompt', e.target.value)}
            rows={6}
            disabled={readOnly}
            className="rounded-xl resize-y min-h-[150px] bg-white border-border"
          />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Configurações técnicas e administrativas avançadas para o comportamento da IA
          </p>
        </div>
      </CardContent>
    </Card>
  );
};